// ITER154 (Feb 2026) — Registro de documentos electrónicos SRI
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Search, RefreshCw } from "lucide-react";
import ExportButton from "@/components/ExportButton";

function defaultDesde() { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); }
function defaultHasta() { return new Date().toISOString().slice(0, 10); }

const TIPO_LABEL = { factura: "Factura", nc: "Nota de Crédito", nd: "Nota de Débito", retencion: "Retención", guia: "Guía Remisión" };
const TIPO_COLOR = { factura: "bg-[#205c45] text-white", nc: "bg-[#b53a25] text-white", nd: "bg-[#b08400] text-white", retencion: "bg-[#2d4a73] text-white", guia: "bg-[#4a4a8a] text-white" };
const ESTADO_STYLE = (e) => {
  if (e === "autorizado") return "bg-[#e9f3ec] text-[#205c45] border-[#a4d9b3]";
  if (e === "anulado") return "bg-[#fde4de] text-[#b53a25] border-[#f2a494]";
  if (e === "rechazado") return "bg-[#fff4e6] text-[#a55700] border-[#f5c684]";
  if (e === "migrado") return "bg-[#eaf2fa] text-[#2d4a73] border-[#a6c4e0]";
  return "bg-[#f3f4f5] text-[#5c7368] border-[#d1dbd6]";
};

export default function DocumentosElectronicos() {
  const [desde, setDesde] = useState(defaultDesde());
  const [hasta, setHasta] = useState(defaultHasta());
  const [tipo, setTipo] = useState("all");
  const [estado, setEstado] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], totales: null, resumen: [] });

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (desde) p.set("desde", desde);
      if (hasta) p.set("hasta", hasta);
      if (tipo && tipo !== "all") p.set("tipo", tipo);
      if (estado && estado !== "all") p.set("estado", estado);
      if (q) p.set("q", q);
      const { data: d } = await api.get(`/reports/documentos-electronicos?${p.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const [checkingId, setCheckingId] = useState(null);
  const consultarSri = async (row) => {
    if (!row.clave_acceso) {
      toast.error("Este documento aún no tiene clave de acceso");
      return;
    }
    const key = `${row.tipo}-${row.id}`;
    setCheckingId(key);
    try {
      const { data: r } = await api.post("/reports/documentos-electronicos/consultar-sri", { tipo: row.tipo, id: row.id });
      if (r.cambio) {
        toast.success(`Estado actualizado: ${r.estado_local_previo} → ${r.estado_local_actual}`, { duration: 5000 });
        await load();
      } else if (r.estado_sri === "AUTORIZADO") {
        toast.success(`SRI confirma AUTORIZADO · Autorización ${r.numero_autorizacion || "—"}`, { duration: 5000 });
      } else {
        const msg = (r.mensajes || []).map(m => m.mensaje).filter(Boolean).join(" · ") || `Estado SRI: ${r.estado_sri || "—"}`;
        toast(msg, { duration: 6000 });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCheckingId(null);
    }
  };

  const cols = [
    { label: "Tipo", accessor: (r) => TIPO_LABEL[r.tipo] || r.tipo },
    { label: "Número", accessor: "numero" },
    { label: "Fecha", accessor: "fecha" },
    { label: "Tercero", accessor: "tercero" },
    { label: "Total", accessor: (r) => `$${(r.total || 0).toFixed(2)}` },
    { label: "Estado SRI", accessor: "estado_sri" },
    { label: "Clave acceso", accessor: (r) => r.clave_acceso || "—" },
  ];

  const resumenPorTipo = useMemo(() => {
    const agg = {};
    (data.resumen || []).forEach(r => {
      const k = r.tipo;
      agg[k] = agg[k] || { tipo: k, total: 0, autorizado: 0, anulado: 0, rechazado: 0, otros: 0 };
      agg[k].total += r.cantidad;
      if (r.estado === "autorizado") agg[k].autorizado += r.cantidad;
      else if (r.estado === "anulado") agg[k].anulado += r.cantidad;
      else if (r.estado === "rechazado") agg[k].rechazado += r.cantidad;
      else agg[k].otros += r.cantidad;
    });
    return Object.values(agg);
  }, [data.resumen]);

  return (
    <div className="space-y-5" data-testid="docs-electronicos-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad · Gerencia</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <FileText className="inline mr-2 mb-1" size={28} />
          Documentos Electrónicos SRI
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">Registro consolidado de facturas, notas de crédito/débito, retenciones y guías con su estado ante el SRI.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-end p-3 rounded-md border border-[#d1dbd6] bg-white">
        <div><Label className="text-[10px] uppercase">Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-8" /></div>
        <div><Label className="text-[10px] uppercase">Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-8" /></div>
        <div>
          <Label className="text-[10px] uppercase">Tipo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="h-8 w-44" data-testid="tipo-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="factura">Facturas</SelectItem>
              <SelectItem value="nc">Notas de Crédito</SelectItem>
              <SelectItem value="nd">Notas de Débito</SelectItem>
              <SelectItem value="retencion">Retenciones</SelectItem>
              <SelectItem value="guia">Guías de Remisión</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Estado SRI</Label>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="h-8 w-40" data-testid="estado-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="autorizado">Autorizado</SelectItem>
              <SelectItem value="anulado">Anulado</SelectItem>
              <SelectItem value="rechazado">Rechazado</SelectItem>
              <SelectItem value="borrador">Borrador</SelectItem>
              <SelectItem value="firmado">Firmado</SelectItem>
              <SelectItem value="migrado">Migrado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]"><Label className="text-[10px] uppercase">Buscar</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N°, cliente, clave acceso…" className="h-8" /></div>
        <Button onClick={load} disabled={loading} className="bg-[#205c45] hover:bg-[#1a4a37] h-8" data-testid="aplicar-filtros"><Search size={14} className="mr-1" />Aplicar</Button>
        <ExportButton title={`Documentos electrónicos ${desde}→${hasta}`} columns={cols} rows={data.items} filename={`docs-electronicos-${desde}-${hasta}`} />
      </div>

      {/* Resumen por tipo */}
      {resumenPorTipo.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {resumenPorTipo.map(r => (
            <div key={r.tipo} className="rounded-md border border-[#d1dbd6] bg-white p-3">
              <Badge className={TIPO_COLOR[r.tipo] || ""}>{TIPO_LABEL[r.tipo] || r.tipo}</Badge>
              <div className="text-xl font-semibold text-[#15241d] tabular-nums mt-2">{r.total}</div>
              <div className="text-[10px] text-[#5c7368] mt-1 space-y-0.5">
                <div>Autorizados: <b className="text-[#205c45]">{r.autorizado}</b></div>
                {r.anulado > 0 && <div>Anulados: <b className="text-[#b53a25]">{r.anulado}</b></div>}
                {r.rechazado > 0 && <div>Rechazados: <b className="text-[#a55700]">{r.rechazado}</b></div>}
                {r.otros > 0 && <div>Otros: <b>{r.otros}</b></div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-x-auto">
        <table className="erp-table">
          <thead><tr>
            <th>Tipo</th><th>N° Documento</th><th>Fecha</th><th>Tercero</th>
            <th className="text-right">Total</th>
            <th>Estado</th>
            <th>Clave acceso</th>
            <th>Fecha autorización</th>
            <th className="w-24 text-right">SRI</th>
          </tr></thead>
          <tbody>
            {data.items.map((it, i) => {
              const key = `${it.tipo}-${it.id}`;
              const checking = checkingId === key;
              return (
              <tr key={`${it.tipo}-${it.id}-${i}`} data-testid={`docs-electronicos-row-${it.tipo}-${it.id}`}>
                <td><Badge className={TIPO_COLOR[it.tipo] || ""}>{TIPO_LABEL[it.tipo] || it.tipo}</Badge></td>
                <td className="font-mono text-xs">{it.numero}</td>
                <td className="text-xs">{it.fecha}</td>
                <td>{it.tercero}</td>
                <td className="text-right tabular-nums font-semibold">${(it.total || 0).toFixed(2)}</td>
                <td><Badge variant="outline" className={ESTADO_STYLE(it.estado_sri) + " capitalize"}>{it.estado_sri}</Badge></td>
                <td className="font-mono text-[10px] break-all">{it.clave_acceso || "—"}</td>
                <td className="text-[10px]">{(it.fecha_autorizacion || "").slice(0, 16).replace("T", " ")}</td>
                <td className="text-right">
                  {it.clave_acceso ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => consultarSri(it)}
                      disabled={checking}
                      data-testid={`consultar-sri-btn-${it.tipo}-${it.id}`}
                      title="Consultar estado real en SRI"
                    >
                      <RefreshCw size={11} className={`${checking ? "animate-spin" : ""} mr-1`} />
                      {checking ? "…" : "Consultar"}
                    </Button>
                  ) : (
                    <span className="text-[10px] text-[#8ba397]">—</span>
                  )}
                </td>
              </tr>
              );
            })}
            {data.items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8ba397]">Sin documentos</td></tr>}
          </tbody>
        </table>
      </div>

      {data.totales && (
        <div className="text-xs text-[#5c7368] flex gap-4 justify-end">
          <span>{data.totales.total_documentos} documentos</span>
          <span>Total: <b className="text-[#15241d]">${data.totales.monto_total.toFixed(2)}</b></span>
        </div>
      )}
    </div>
  );
}
